import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, Subject } from 'rxjs';
import { map, distinctUntilChanged, shareReplay, debounceTime } from 'rxjs/operators';
import { AuthService, UserData, Credits, UserDataItem, TatUserDataChanges } from './auth.service';
import { Company } from '../models/company.models';
import { UserJob } from '../models/job.models';
import { Tool } from '../models/tool.models';
import { LoggerService } from './logger.service';
import { ErrorHandlerService } from './error-handler.service';
import { TranslationService } from './translation.service';
import { NetworkService } from './network.service';
import { OfflineQueueService } from './offline-queue.service';
import { CACHE_DURATION } from '../constants/app.constants';

/**
 * Application-wide data state
 */
export interface AppDataState {
  userData: UserData | null;
  companyData: Company | null;
  jobs: { [key: string]: UserJob } | null;
  tools: { [key: string]: Tool } | null;
  userResults: { [toolName: string]: { [uid: string]: UserDataItem } } | null;
  tatUserDataChanges: TatUserDataChanges | null;
  credits: Credits | null;
  lastUpdate: number;
  isRefreshing: boolean;
  error: Error | null;
}

/**
 * Initial state
 */
const initialState: AppDataState = {
  userData: null,
  companyData: null,
  jobs: null,
  tools: null,
  userResults: null,
  tatUserDataChanges: null,
  credits: null,
  lastUpdate: 0,
  isRefreshing: false,
  error: null
};

/**
 * Options for refreshData()
 */
export interface RefreshOptions {
  /**
   * Results (getAllTatUserData) are only fetched when the last results refresh is older than this (ms).
   * Default: CACHE_DURATION.RESULTS_REFRESH. 0 = always, Infinity = status only.
   */
  resultsMaxAge?: number;
}

/** Refresh error that must not be retried (server reachable, but not logged in) */
class NonRetryableRefreshError extends Error {}

/**
 * Data State Service
 * 
 * Centralized state management for all application data.
 * Provides Observable-based state management with automatic updates,
 * optimistic updates, and background synchronization.
 * 
 * Features:
 * - Observable-based state management
 * - Deduplicated refresh requests
 * - Optimistic updates with rollback
 * - Automatic cache invalidation
 * - Background synchronization: status every 60 s, results every 5 min,
 *   paused while the app is hidden, immediate refresh on resume / going online
 */
@Injectable({
  providedIn: 'root'
})
export class DataStateService {
  private readonly authService = inject(AuthService);
  private readonly logger = inject(LoggerService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly translationService = inject(TranslationService);
  private readonly networkService = inject(NetworkService);
  private readonly offlineQueue = inject(OfflineQueueService);

  // State management
  private readonly stateSubject = new BehaviorSubject<AppDataState>(initialState);
  public readonly state$: Observable<AppDataState> = this.stateSubject.asObservable().pipe(
    shareReplay(1)
  );

  // Specific data observables
  public readonly userData$: Observable<UserData | null> = this.state$.pipe(
    map(state => state.userData),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly companyData$: Observable<Company | null> = this.state$.pipe(
    map(state => state.companyData),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly jobs$: Observable<{ [key: string]: UserJob } | null> = this.state$.pipe(
    map(state => state.jobs),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly tools$: Observable<{ [key: string]: Tool } | null> = this.state$.pipe(
    map(state => state.tools),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly userResults$: Observable<{ [toolName: string]: { [uid: string]: UserDataItem } } | null> = this.state$.pipe(
    map(state => state.userResults),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly tatUserDataChanges$: Observable<TatUserDataChanges | null> = this.state$.pipe(
    map(state => state.tatUserDataChanges),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly credits$: Observable<Credits | null> = this.state$.pipe(
    map(state => state.credits),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly isRefreshing$: Observable<boolean> = this.state$.pipe(
    map(state => state.isRefreshing),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly error$: Observable<Error | null> = this.state$.pipe(
    map(state => state.error),
    distinctUntilChanged(),
    shareReplay(1)
  );

  public readonly lastUpdate$: Observable<number> = this.state$.pipe(
    map(state => state.lastUpdate),
    distinctUntilChanged(),
    shareReplay(1)
  );

  // Refresh deduplication
  private refreshPromise: Promise<void> | null = null;
  private backgroundRefreshInterval: number | null = null;
  private readonly BACKGROUND_REFRESH_INTERVAL = CACHE_DURATION.BACKGROUND_SYNC;
  private readonly RESULTS_REFRESH_INTERVAL = CACHE_DURATION.RESULTS_REFRESH;
  private readonly RESUME_REFRESH_MIN_GAP = CACHE_DURATION.RESUME_REFRESH_MIN_GAP;
  private lastResultsRefresh = 0;
  private lastSyncTrigger = 0;
  private lifecycleListenersAttached = false;

  // Retry configuration
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY_BASE = 1000; // Base delay in ms (1 second)
  private readonly RETRY_DELAY_MAX = 10000; // Maximum delay in ms (10 seconds)

  // Performance optimization: Debouncing for batch updates
  private readonly updateDebounceTime = 100; // 100ms debounce for local updates
  private pendingUpdates: Partial<AppDataState> = {};
  private updateDebounceSubject = new Subject<void>();
  private updateDebounceSubscription = this.updateDebounceSubject.pipe(
    debounceTime(this.updateDebounceTime)
  ).subscribe(() => {
    if (Object.keys(this.pendingUpdates).length > 0) {
      this.updateState(this.pendingUpdates);
      this.pendingUpdates = {};
    }
  });

  constructor() {
    // Initialize state from AuthService
    this.initializeState();

    // Requests sent from the offline queue change server data (e.g. createJob): reload status afterwards
    this.offlineQueue.queueProcessed$.subscribe(() => {
      if (!this.authService.isLoggedIn()) return;
      this.authService.invalidateStatusCache();
      this.refreshData({ resultsMaxAge: Infinity }).catch(error => {
        this.logger.error('DataStateService: Refresh after offline queue failed', error);
      });
    });
  }

  /**
   * Initialize state from AuthService
   */
  private initializeState(): void {
    const userData = this.authService.getUserData();
    
    // Set language from database (null means no language set, will use browser/default)
    if (userData && 'language' in userData) {
      this.translationService.setLanguageFromDatabase(userData.language || null);
    }
    
    const currentState: AppDataState = {
      userData: userData,
      companyData: this.authService.getCompanyData(),
      jobs: this.authService.getJobs(),
      tools: this.authService.getTools(),
      userResults: this.authService.getUserResults(),
      tatUserDataChanges: this.authService.getLastTatUserDataChanges(),
      credits: this.authService.getCredits(),
      lastUpdate: Date.now(),
      isRefreshing: false,
      error: null
    };

    this.stateSubject.next(currentState);
  }

  /**
   * Get current state synchronously
   */
  getState(): AppDataState {
    return this.stateSubject.value;
  }

  /**
   * Get specific data synchronously
   */
  getUserData(): UserData | null {
    return this.stateSubject.value.userData;
  }

  getCompanyData(): Company | null {
    return this.stateSubject.value.companyData;
  }

  getJobs(): { [key: string]: UserJob } | null {
    return this.stateSubject.value.jobs;
  }

  getTools(): { [key: string]: Tool } | null {
    return this.stateSubject.value.tools;
  }

  getUserResults(): { [toolName: string]: { [uid: string]: UserDataItem } } | null {
    return this.stateSubject.value.userResults;
  }

  getTatUserDataChanges(): TatUserDataChanges | null {
    return this.stateSubject.value.tatUserDataChanges;
  }

  getCredits(): Credits | null {
    return this.stateSubject.value.credits;
  }

  /**
   * Refresh status data (user, company, jobs, tools) and, if older than
   * `resultsMaxAge`, the results (getAllTatUserData).
   * Deduplicates simultaneous refresh requests.
   */
  async refreshData(options: RefreshOptions = {}): Promise<void> {
    const resultsMaxAge = options.resultsMaxAge ?? this.RESULTS_REFRESH_INTERVAL;

    while (this.refreshPromise) {
      this.logger.log('DataStateService: Refresh already in progress, waiting for it');
      await this.refreshPromise.catch(() => {});
      // The running refresh may have skipped the results; only refresh again if they are still too old
      if (!this.areResultsStale(resultsMaxAge)) {
        return;
      }
    }

    this.refreshPromise = this.performRefresh(this.areResultsStale(resultsMaxAge));

    try {
      await this.refreshPromise;
    } catch (error) {
      this.logger.error('DataStateService: Refresh failed', error);
      this.updateState({ error: error instanceof Error ? error : new Error(String(error)) });
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Refresh the results (and status). `force` bypasses the AuthService results cache,
   * e.g. after an action that changed the results on the server.
   */
  async refreshResults(force: boolean = false): Promise<void> {
    if (force) {
      this.authService.invalidateTatUserDataCache();
    }
    await this.refreshData({ resultsMaxAge: 0 });
  }

  private areResultsStale(maxAge: number): boolean {
    return Date.now() - this.lastResultsRefresh >= maxAge;
  }

  /**
   * Perform the actual refresh operation with retry logic
   */
  private async performRefresh(includeResults: boolean, retryCount: number = 0): Promise<void> {
    this.updateState({ isRefreshing: true, error: null });

    try {
      // Fetch status and (if requested) tat user data in parallel
      const [statusResponse, tatUserDataResult] = await Promise.all([
        this.authService.fetchStatus(),
        includeResults ? this.authService.fetchTatUserDataWithChanges() : Promise.resolve(null)
      ]);

      const statusData = statusResponse.DATA;
      
      if (statusData?.auth_staus === true && statusData.message === 'logged_in') {
        // Flush any pending updates before applying refresh
        this.flushPendingUpdates();
        
        // Get fresh userData and set language from database
        const userData = this.authService.getUserData();
        // Set language from database (null means no language set, will use browser/default)
        if (userData && 'language' in userData) {
          this.translationService.setLanguageFromDatabase(userData.language || null);
        }
        
        // Update state with fresh data from AuthService (immediate for refresh)
        const newState: Partial<AppDataState> = {
          userData: userData,
          companyData: this.authService.getCompanyData(),
          jobs: this.authService.getJobs(),
          tools: this.authService.getTools(),
          credits: this.authService.getCredits(),
          lastUpdate: Date.now(),
          isRefreshing: false,
          error: null
        };
        if (tatUserDataResult) {
          newState.userResults = this.authService.getUserResults();
          newState.tatUserDataChanges = tatUserDataResult.changes;
          this.lastResultsRefresh = Date.now();
        }

        this.updateState(newState, true); // Immediate update for refresh
        this.logger.log(`DataStateService: Data refreshed successfully${includeResults ? ' (incl. results)' : ''}`);
      } else if (statusResponse.STATUS !== 'ERROR') {
        // Server answered, but the session is not valid: retrying does not help
        throw new NonRetryableRefreshError(`Invalid status response: ${statusResponse.STATUS}`);
      } else {
        throw new Error('Invalid status response');
      }
    } catch (error) {
      const retryable = !(error instanceof NonRetryableRefreshError) && this.networkService.isOnline();

      // Retry logic with exponential backoff
      if (retryable && retryCount < this.MAX_RETRIES) {
        const delay = Math.min(
          this.RETRY_DELAY_BASE * Math.pow(2, retryCount),
          this.RETRY_DELAY_MAX
        );
        
        this.logger.warn(
          `DataStateService: Refresh failed (attempt ${retryCount + 1}/${this.MAX_RETRIES}), retrying in ${delay}ms...`,
          error
        );
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry
        return this.performRefresh(includeResults, retryCount + 1);
      } else {
        this.logger.error('DataStateService: Refresh failed', error);
        this.updateState({
          isRefreshing: false,
          error: error instanceof Error ? error : new Error(String(error))
        }, true); // Immediate update for errors
        throw error;
      }
    }
  }

  /**
   * Update state partially
   * Uses batching for multiple rapid updates to improve performance
   * isRefreshing and error states are always updated immediately for UI responsiveness
   */
  private updateState(updates: Partial<AppDataState>, immediate: boolean = false): void {
    // Update language from database if userData is being updated
    // Check if language property exists (including null/undefined) in the update
    if (updates.userData && 'language' in updates.userData) {
      // Set language from database (null means no language set, will use browser/default)
      this.translationService.setLanguageFromDatabase(updates.userData.language || null);
    }
    
    // Always update isRefreshing and error immediately for UI responsiveness
    const hasCriticalUpdates = 'isRefreshing' in updates || 'error' in updates;
    
    if (immediate || hasCriticalUpdates) {
      // Immediate update (for critical state changes like errors, refreshing status)
      const currentState = this.stateSubject.value;
      const newState: AppDataState = {
        ...currentState,
        ...updates
      };
      this.stateSubject.next(newState);
    } else {
      // Batch updates for performance
      this.pendingUpdates = { ...this.pendingUpdates, ...updates };
      this.updateDebounceSubject.next();
    }
  }

  /**
   * Flush pending updates immediately
   */
  private flushPendingUpdates(): void {
    if (Object.keys(this.pendingUpdates).length > 0) {
      const currentState = this.stateSubject.value;
      const newState: AppDataState = {
        ...currentState,
        ...this.pendingUpdates
      };
      this.stateSubject.next(newState);
      this.pendingUpdates = {};
    }
  }

  /**
   * Update data optimistically (local update before server confirmation)
   * 
   * @param key - Key of the data to update
   * @param value - New value
   */
  updateDataLocally<K extends keyof AppDataState>(
    key: K,
    value: AppDataState[K]
  ): void {
    this.updateState({ [key]: value } as Partial<AppDataState>);
    this.logger.log(`DataStateService: Updated ${key} locally`);
  }

  /**
   * Rollback optimistic update on error
   * 
   * @param key - Key of the data to rollback
   * @param previousValue - Previous value to restore
   */
  rollbackUpdate<K extends keyof AppDataState>(
    key: K,
    previousValue: AppDataState[K]
  ): void {
    this.updateState({ [key]: previousValue } as Partial<AppDataState>);
    this.logger.log(`DataStateService: Rolled back ${key}`);
  }

  /**
   * Perform optimistic update with automatic rollback on error
   * 
   * @param key - Key of the data to update
   * @param newValue - New value to set optimistically
   * @param updateFn - Async function that performs the actual update (should throw on error)
   * @returns Promise that resolves when update completes or rejects on error (with automatic rollback)
   * 
   * @example
   * ```typescript
   * const previousValue = dataStateService.getCompanyData();
   * await dataStateService.updateWithRollback(
   *   'companyData',
   *   { ...previousValue, company: 'New Name' },
   *   () => authService.updateCompanyData({ company: 'New Name' })
   * );
   * ```
   */
  async updateWithRollback<K extends keyof AppDataState>(
    key: K,
    newValue: AppDataState[K],
    updateFn: () => Promise<any>
  ): Promise<void> {
    const previousValue = this.stateSubject.value[key];
    
    // Optimistically update local state
    this.updateDataLocally(key, newValue);
    
    try {
      // Perform actual update
      await updateFn();
      
      // Refresh data to ensure consistency
      await this.refreshData();
      
      this.logger.log(`DataStateService: Optimistic update for ${key} confirmed`);
    } catch (error) {
      // Rollback on error
      this.rollbackUpdate(key, previousValue);
      this.logger.error(`DataStateService: Optimistic update for ${key} failed, rolled back`, error);
      throw error;
    }
  }

  /**
   * Start background synchronization (idempotent).
   * Runs app-wide while logged in, independent of the current page:
   * status every 60 s, results when older than 5 min. Paused while the app is hidden;
   * refreshes immediately when the app becomes visible again or the device goes online.
   */
  startBackgroundSync(): void {
    this.attachLifecycleListeners();

    if (this.backgroundRefreshInterval !== null) {
      return;
    }

    this.logger.log('DataStateService: Starting background sync');
    
    this.backgroundRefreshInterval = window.setInterval(() => {
      this.syncTick();
    }, this.BACKGROUND_REFRESH_INTERVAL);
  }

  /**
   * Stop background synchronization
   */
  stopBackgroundSync(): void {
    if (this.backgroundRefreshInterval !== null) {
      clearInterval(this.backgroundRefreshInterval);
      this.backgroundRefreshInterval = null;
      this.logger.log('DataStateService: Background sync stopped');
    }
  }

  /**
   * One background sync run. Skipped while hidden; stops the sync after logout.
   */
  private syncTick(minGap: number = 0): void {
    if (this.backgroundRefreshInterval === null) {
      return;
    }
    if (!this.authService.isLoggedIn()) {
      this.clearState();
      return;
    }
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }
    const now = Date.now();
    if (now - this.lastSyncTrigger < minGap) {
      return;
    }
    this.lastSyncTrigger = now;

    this.refreshData().catch(error => {
      this.logger.error('DataStateService: Background sync error', error);
    });
  }

  /**
   * Refresh when the app/tab becomes visible again or the device comes back online.
   * Listeners are attached once and only act while the background sync is running.
   */
  private attachLifecycleListeners(): void {
    if (this.lifecycleListenersAttached || typeof window === 'undefined') {
      return;
    }
    this.lifecycleListenersAttached = true;

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.syncTick(this.RESUME_REFRESH_MIN_GAP);
      }
    });
    window.addEventListener('online', () => this.syncTick(this.RESUME_REFRESH_MIN_GAP));
  }

  /**
   * Clear all state (e.g., on logout)
   */
  clearState(): void {
    // Flush pending updates before clearing
    this.flushPendingUpdates();
    
    this.stateSubject.next(initialState);
    this.stopBackgroundSync();
    this.refreshPromise = null;
    this.pendingUpdates = {};
    this.lastResultsRefresh = 0;
    this.lastSyncTrigger = 0;
    this.logger.log('DataStateService: State cleared');
  }
}
